import React, { useMemo } from 'react';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Spade from 'lucide-react/dist/esm/icons/spade.js';
import Grid from 'lucide-react/dist/esm/icons/grid.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus.js';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.js';
import Mail from 'lucide-react/dist/esm/icons/mail.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { usePageSections } from '../../../hooks/usePageSections';
import { resolveCtaManualEnvelopeMode } from '../../../utils/ctaHelpers';
import {
  HowToPlayDetailedTitle,
  HowToPlaySectionRegisterTitle,
  HowToPlaySectionRegisterStep1,
  HowToPlaySectionRegisterStep2,
  HowToPlaySectionRegisterStep3,
  HowToPlaySectionRegisterStep4,
  HowToPlaySectionRegisterStep5,
  HowToPlaySectionRegisterStep6,
} from '../../../content/keys';

const FALLBACKS = {
  [HowToPlayDetailedTitle]: 'Detailed Instructions',
  [HowToPlaySectionRegisterTitle]: 'Creating Your Account',
  [HowToPlaySectionRegisterStep1]: 'Click the "Sign Up" button on the homepage',
  [HowToPlaySectionRegisterStep2]: 'Enter your email address and create a password',
  [HowToPlaySectionRegisterStep3]: 'Verify your email by clicking the confirmation link',
  [HowToPlaySectionRegisterStep4]: 'Complete your profile with required contact information',
  [HowToPlaySectionRegisterStep5]: 'Sign in anytime to view your active tickets and receipts',
  [HowToPlaySectionRegisterStep6]: "You're ready to start playing!",
};

function buildGameDetailedConfig(gameType, isManualEnvelopeMode, isGuest) {
  const variantKey = gameType === 'chaseTheAce' && isManualEnvelopeMode
    ? 'chaseTheAceManual'
    : (gameType || 'classic5050');

  const prefix = `howToPlay.detailed.${variantKey}`;
  const checkoutStepFallback = isGuest
    ? 'Proceed to checkout and enter your contact details as a guest — no account or password required'
    : 'Review your selections and proceed to our secure checkout';
  const checkoutStepKey = isGuest ? `${prefix}.step4Guest` : `${prefix}.step4`;

  switch (variantKey) {
    case 'squareGame':
      return {
        icon: Grid,
        badgeKey: `${prefix}.badge`,
        badgeFallback: '100 Square Board',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Reserve your favourite square numbers on the live grid and lock them in at checkout.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Live interactive grid' },
          { key: `${prefix}.highlight2`, fallback: 'Instant square hold timer' },
          { key: `${prefix}.highlight3`, fallback: 'Immediate email confirmation' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play the Square Game (No Account Needed)'
          : 'How to Play the Square Game',
        steps: [
          { key: `${prefix}.step1`, fallback: 'View the live Square Grid and tap any available square number to select it' },
          { key: `${prefix}.step2`, fallback: 'Your chosen square is temporarily held for you with a countdown timer while you check out' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then click Continue to Checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card before the hold timer expires' },
          { key: `${prefix}.step6`, fallback: 'Receive your confirmed square number and official receipt by email — when the draw runs, the winning square takes the prize!' },
        ],
      };

    case 'chaseTheAce':
      return {
        icon: Spade,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Weekly Draw + Progressive Jackpot',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Buy your tickets, pick an unopened envelope from the deck, and chase the Progressive Jackpot.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Guaranteed weekly prize winner' },
          { key: `${prefix}.highlight2`, fallback: 'Choose your lucky envelope' },
          { key: `${prefix}.highlight3`, fallback: 'Rolling progressive jackpot' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Chase the Ace (No Account Needed)'
          : 'How to Play Chase the Ace',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage and select your preferred ticket package' },
          { key: `${prefix}.step2`, fallback: 'Pick your lucky envelope from the remaining unopened envelopes in the deck' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your ticket numbers, chosen envelope number, and official receipt by email — if your ticket is drawn, you win the weekly prize and your envelope is opened for the Progressive Jackpot!' },
        ],
      };

    case 'chaseTheAceManual':
      return {
        icon: Spade,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Weekly Draw + Envelope Reveal',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Enter the weekly ticket draw for a guaranteed weekly prize and a shot at the Progressive Jackpot.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Guaranteed weekly prize winner' },
          { key: `${prefix}.highlight2`, fallback: 'Winner envelope reveal' },
          { key: `${prefix}.highlight3`, fallback: 'Rolling progressive jackpot' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Chase the Ace (No Account Needed)'
          : 'How to Play Chase the Ace',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage and select your ticket package for the weekly draw' },
          { key: `${prefix}.step2`, fallback: 'Choose the number of ticket packages you want to purchase' },
          { key: `${prefix}.step3`, fallback: 'Select a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official ticket numbers and receipt by email — if your ticket is drawn, you win the weekly prize and an envelope is revealed for the Progressive Jackpot!' },
        ],
      };

    case 'modified5050':
      return {
        icon: Calendar,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Multi-Week 50/50 Series',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Enter the current 50/50 draw or lock in your tickets across multiple upcoming weekly draws at once.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: '50% of sales to the winner' },
          { key: `${prefix}.highlight2`, fallback: 'Multi-week advance entry option' },
          { key: `${prefix}.highlight3`, fallback: 'Instant email ticket delivery' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Modified 50/50 (No Account Needed)'
          : 'How to Play Modified 50/50',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage and choose your ticket package' },
          { key: `${prefix}.step2`, fallback: 'Select how many consecutive weekly draws you want your tickets entered into' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then review your multi-draw total' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official ticket numbers and receipt by email — your tickets are automatically entered into each selected weekly draw!' },
        ],
      };

    case 'progressive5050':
      return {
        icon: TrendingUp,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Rolling Progressive 50/50',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Every ticket builds the prize pot and gives you a shot at the growing Progressive Jackpot.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Growing rollover jackpot' },
          { key: `${prefix}.highlight2`, fallback: 'Value-packed ticket bundles' },
          { key: `${prefix}.highlight3`, fallback: 'Instant email confirmation' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Progressive 50/50 (No Account Needed)'
          : 'How to Play Progressive 50/50',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage to enter the current Progressive 50/50 draw' },
          { key: `${prefix}.step2`, fallback: 'Select your preferred ticket package and quantity to maximize your entries' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official ticket numbers and receipt by email — if the progressive jackpot is not hit, it rolls over to the next draw!' },
        ],
      };

    case 'eliminationDraw':
      return {
        icon: Trophy,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Reverse Elimination Draw',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Tickets are drawn one by one for milestone prizes until the final ticket standing wins the Grand Prize!',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Last ticket standing wins Grand Prize' },
          { key: `${prefix}.highlight2`, fallback: 'Milestone prizes throughout the draw' },
          { key: `${prefix}.highlight3`, fallback: 'Live elimination tracking' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play the Elimination Draw (No Account Needed)'
          : 'How to Play the Elimination Draw',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage to view available Elimination Draw packages' },
          { key: `${prefix}.step2`, fallback: 'Select your ticket bundle and quantity — every ticket is a separate entry in the draw' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official ticket numbers and receipt by email — win milestone prizes along the way or be the last ticket remaining to win the Grand Prize!' },
        ],
      };

    case 'prizeDraw':
      return {
        icon: Gift,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Featured Prize Raffle',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Enter to win the featured Grand Prize plus any Early Bird and bonus prize draws.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Featured Grand Prize packages' },
          { key: `${prefix}.highlight2`, fallback: 'Eligible for Early Bird draws' },
          { key: `${prefix}.highlight3`, fallback: 'Instant official email receipt' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play the Prize Draw (No Account Needed)'
          : 'How to Play the Prize Draw',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Explore the featured Grand Prize and any Early Bird prizes on the homepage' },
          { key: `${prefix}.step2`, fallback: 'Click "Buy Tickets" and select the ticket bundle and quantity you want' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official ticket numbers and receipt immediately by email — winners are drawn and contacted directly!' },
        ],
      };

    case 'superballBingo':
      return {
        icon: Sparkles,
        badgeKey: `${prefix}.badge`,
        badgeFallback: 'Session Prize + Golden Superball',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Get your tickets and Superball number for a shot at the session prize plus the Progressive Superball Jackpot!',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Guaranteed session prize winner' },
          { key: `${prefix}.highlight2`, fallback: 'Superball numbers 1 to 75' },
          { key: `${prefix}.highlight3`, fallback: 'Progressive Superball jackpot' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Superball Bingo (No Account Needed)'
          : 'How to Play Superball Bingo',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage and choose your ticket package' },
          { key: `${prefix}.step2`, fallback: 'Pick your lucky Superball number from 1 to 75 (or have one randomly assigned)' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner if applicable, then continue to checkout' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your ticket numbers, Superball number, and receipt by email — win the session prize if drawn, plus the Progressive Jackpot if your Superball matches!' },
        ],
      };

    case 'classic5050':
    default:
      return {
        icon: Ticket,
        badgeKey: `${prefix}.badge`,
        badgeFallback: '50/50 Cash Raffle',
        summaryKey: `${prefix}.summary`,
        summaryFallback: 'Half of the total ticket sales goes to one lucky winner and half supports the charitable cause.',
        highlights: [
          { key: `${prefix}.highlight1`, fallback: 'Winner takes 50% of the pot' },
          { key: `${prefix}.highlight2`, fallback: '50% supports the charity' },
          { key: `${prefix}.highlight3`, fallback: 'Instant email ticket delivery' },
        ],
        titleKey: isGuest ? `${prefix}.guestTitle` : `${prefix}.title`,
        fallbackTitle: isGuest
          ? 'How to Play Classic 50/50 (No Account Needed)'
          : 'How to Play Classic 50/50',
        steps: [
          { key: `${prefix}.step1`, fallback: 'Click "Buy Tickets" on the homepage to view available 50/50 ticket packages' },
          { key: `${prefix}.step2`, fallback: 'Select your preferred ticket bundle and quantity — larger bundles offer the best value' },
          { key: `${prefix}.step3`, fallback: 'Choose a supporting partner from the dropdown if applicable' },
          { key: checkoutStepKey, fallback: checkoutStepFallback },
          { key: `${prefix}.step5`, fallback: 'Complete payment securely with your credit or debit card' },
          { key: `${prefix}.step6`, fallback: 'Receive your official 50/50 ticket numbers and receipt immediately by email — one winning ticket takes 50% of the pot!' },
        ],
      };
  }
}

export default function HowToVideoSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const { gameType } = useGameType();
  const { siteDetails } = useSiteConfig();
  const { sections: homeSections } = usePageSections('chasetheace_home');
  const isManualEnvelopeMode = gameType === 'chaseTheAce' && resolveCtaManualEnvelopeMode(siteDetails, homeSections);
  const isGuest = isGuestCheckoutEnabled();

  const t = (key, fallback) => {
    const v = c(key);
    if (v) return v;
    const trVal = tr(key);
    if (trVal && trVal !== key) return trVal;
    return fallback ?? FALLBACKS[key] ?? '';
  };

  const gameDetailed = useMemo(
    () => buildGameDetailedConfig(gameType, isManualEnvelopeMode, isGuest),
    [gameType, isManualEnvelopeMode, isGuest]
  );

  const howToSections = useMemo(() => {
    const sectionsList = [];
    if (!isGuest) {
      sectionsList.push({
        id: 'register',
        icon: UserPlus,
        badgeKey: 'howToPlay.detailed.register.badge',
        badgeFallback: 'Quick & Free Setup',
        summaryKey: 'howToPlay.detailed.register.summary',
        summaryFallback: 'Create your player account once to purchase tickets faster and view your order history anytime.',
        highlights: [
          { key: 'howToPlay.detailed.register.highlight1', fallback: 'Fast email verification' },
          { key: 'howToPlay.detailed.register.highlight2', fallback: 'Secure player profile' },
          { key: 'howToPlay.detailed.register.highlight3', fallback: 'Automatic winner alerts' },
        ],
        titleKey: HowToPlaySectionRegisterTitle,
        fallbackTitle: FALLBACKS[HowToPlaySectionRegisterTitle],
        steps: [
          { key: HowToPlaySectionRegisterStep1, fallback: FALLBACKS[HowToPlaySectionRegisterStep1] },
          { key: HowToPlaySectionRegisterStep2, fallback: FALLBACKS[HowToPlaySectionRegisterStep2] },
          { key: HowToPlaySectionRegisterStep3, fallback: FALLBACKS[HowToPlaySectionRegisterStep3] },
          { key: HowToPlaySectionRegisterStep4, fallback: FALLBACKS[HowToPlaySectionRegisterStep4] },
          { key: HowToPlaySectionRegisterStep5, fallback: FALLBACKS[HowToPlaySectionRegisterStep5] },
          { key: HowToPlaySectionRegisterStep6, fallback: FALLBACKS[HowToPlaySectionRegisterStep6] },
        ],
      });
    }

    sectionsList.push({
      id: 'game-purchase',
      ...gameDetailed,
    });

    return sectionsList;
  }, [isGuest, gameDetailed]);

  return (
    <div className="mb-8 sm:mb-16 md:mb-20">
      <InlineTranslationEdit
        translationKey={HowToPlayDetailedTitle}
        value={t(HowToPlayDetailedTitle, FALLBACKS[HowToPlayDetailedTitle])}
        fallback={FALLBACKS[HowToPlayDetailedTitle]}
        editMode={editMode}
        onUpdate={handleTranslationUpdate}
        className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white text-center mb-6 sm:mb-12 block"
      />

      <div className="space-y-8 sm:space-y-16">
        {howToSections.map((sec, index) => {
          const SectionIcon = sec.icon || Ticket;
          return (
            <div
              key={sec.id || index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-6 sm:gap-8 items-stretch`}
            >
              <div className="w-full lg:w-5/12 flex">
                <div
                  className="relative w-full rounded-2xl overflow-hidden shadow-xl p-6 sm:p-8 flex flex-col justify-between text-white border border-white/15"
                  style={{
                    background: 'linear-gradient(135deg, var(--charity-primary, #1e40af) 0%, var(--charity-primary-dark, #1e3a8a) 100%)',
                  }}
                >
                  <div className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/10 blur-2xl" />
                  <div className="pointer-events-none absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-black/20 blur-2xl" />

                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs font-semibold uppercase tracking-wider mb-5">
                      <SectionIcon className="w-3.5 h-3.5" />
                      <InlineTranslationEdit
                        translationKey={sec.badgeKey}
                        value={t(sec.badgeKey, sec.badgeFallback)}
                        fallback={sec.badgeFallback}
                        editMode={editMode}
                        onUpdate={handleTranslationUpdate}
                      />
                    </div>

                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center mb-4 shadow-lg">
                      <SectionIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                    </div>

                    <p className="text-sm sm:text-base text-white/90 leading-relaxed mb-6">
                      <InlineTranslationEdit
                        translationKey={sec.summaryKey}
                        value={t(sec.summaryKey, sec.summaryFallback)}
                        fallback={sec.summaryFallback}
                        editMode={editMode}
                        onUpdate={handleTranslationUpdate}
                      />
                    </p>
                  </div>

                  <div className="relative z-10 space-y-2.5 pt-4 border-t border-white/15">
                    {(sec.highlights || []).map((hl, hlIdx) => {
                      const HlIcon = hlIdx === 0 ? Sparkles : hlIdx === 1 ? ShieldCheck : Mail;
                      return (
                        <div key={hl.key} className="flex items-center gap-2.5 text-xs sm:text-sm text-white/95 font-medium">
                          <div className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                            <HlIcon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <InlineTranslationEdit
                            translationKey={hl.key}
                            value={t(hl.key, hl.fallback)}
                            fallback={hl.fallback}
                            editMode={editMode}
                            onUpdate={handleTranslationUpdate}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-7/12">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-6 flex items-center gap-2.5 sm:gap-3">
                  <span
                    className="w-1.5 h-6 sm:h-8 rounded-full"
                    style={{ background: 'linear-gradient(to bottom, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
                  />
                  <InlineTranslationEdit
                    translationKey={sec.titleKey}
                    value={t(sec.titleKey, sec.fallbackTitle)}
                    fallback={sec.fallbackTitle}
                    editMode={editMode}
                    onUpdate={handleTranslationUpdate}
                  />
                </h3>
                <ol className="space-y-2 sm:space-y-3.5">
                  {sec.steps.map((stepItem, stepIndex) => (
                    <li
                      key={stepItem.key || stepIndex}
                      className="flex items-start gap-3 sm:gap-4 bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-xl shadow-sm sm:shadow-md border border-gray-200 dark:border-gray-700"
                    >
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5"
                        style={{ background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-accent, #06b6d4))' }}
                      >
                        {stepIndex + 1}
                      </span>
                      <InlineTranslationEdit
                        translationKey={stepItem.key}
                        value={t(stepItem.key, stepItem.fallback)}
                        fallback={stepItem.fallback}
                        editMode={editMode}
                        onUpdate={handleTranslationUpdate}
                        className="text-xs sm:text-base text-gray-700 dark:text-gray-300"
                      />
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
